import { InternalAxiosRequestConfig } from "axios";
import { ADMIN_STORAGE_KEYS } from "./storageKeys";
import { ClientHintBrand, ClientHintValues, NavigatorUserAgentData } from "../utils/types";

const WRITE_METHODS = ["post", "patch", "put", "delete"];

const readDeviceId = (): string => {
  try {
    const storedDeviceId = localStorage.getItem(ADMIN_STORAGE_KEYS.deviceId);
    if (storedDeviceId) return storedDeviceId;

    const newDeviceId = crypto.randomUUID();
    localStorage.setItem(ADMIN_STORAGE_KEYS.deviceId, newDeviceId);
    return newDeviceId;
  } catch {
    return crypto.randomUUID();
  }
};

const pickBrowser = (brands: ClientHintBrand[] = []) => {
  const realBrands = brands.filter(({ brand }) => !brand.includes("Not"));
  const namedBrand = realBrands.find(({ brand }) => brand !== "Chromium") ?? realBrands[0];
  return namedBrand ? `${namedBrand.brand} ${namedBrand.version}` : "";
};

const readArchitecture = ({ architecture, bitness }: ClientHintValues) => {
  if (!architecture) return "";
  return bitness ? `${architecture} ${bitness}-bit` : architecture;
};

const loadClientHintHeaders = async (): Promise<Record<string, string>> => {
  const userAgentData = (navigator as Navigator & { userAgentData?: NavigatorUserAgentData }).userAgentData;
  if (!userAgentData) return {};

  try {
    const hints = await userAgentData.getHighEntropyValues([
      "platformVersion",
      "model",
      "architecture",
      "bitness",
      "fullVersionList",
    ]);
    return {
      "X-Client-Platform": hints.platform ?? "",
      "X-Client-Platform-Version": hints.platformVersion ?? "",
      "X-Client-Browser": pickBrowser(hints.fullVersionList),
      "X-Client-Model": hints.model ?? "",
      "X-Client-Architecture": readArchitecture(hints),
    };
  } catch {
    return {};
  }
};

let clientHintHeadersReady: Promise<Record<string, string>> | null = null;

export const addClientInfoHeaders = async (config: InternalAxiosRequestConfig) => {
  if (!WRITE_METHODS.includes((config.method ?? "get").toLowerCase())) {
    return config;
  }

  config.headers.set("X-Device-Id", readDeviceId());
  clientHintHeadersReady ??= loadClientHintHeaders();
  const clientHintHeaders = await clientHintHeadersReady;
  for (const [name, value] of Object.entries(clientHintHeaders)) {
    if (value) config.headers.set(name, value);
  }
  return config;
};
